import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { Stepper } from '@/components/Stepper';
import { JOURNEY_STEP } from '@/components/stepper.steps';
import type { TranslationKey } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

/**
 * The three things the candidate has to DO, in order, before the scan screen can work
 * [frame `Step 1` / `Step 2` / `Step 3`, 448-wide instances 44px apart].
 *
 * A module constant rather than an inline array: the sequence is the meaning — fetch the packet, hold the
 * barcode up, check the digits — and a numbered list assembled at the call site is one refactor away from
 * being reordered by whoever is tidying the JSX.
 */
const STEPS: readonly TranslationKey[] = [
  'payCardWhy.step1',
  'payCardWhy.step2',
  'payCardWhy.step3',
];

/**
 * *"How you'll get paid"* — the explanation that has to come BEFORE the scan
 * [KPC-1; `11 · Why a pay card` — Desktop 12389:15576 · iPad 12389:15677 · Mobile 12389:15778, all three
 * resolved by NAME on the kiosk page and read live 2026-09-14].
 *
 * The scan screen used to open cold. It asks the candidate to hold a barcode up to a camera, and a
 * first-time candidate has no card: the packets are in a box beside the kiosk and nothing anywhere told
 * them to take one. That instruction is this screen's whole reason to exist — reported by the team
 * reviewing the running kiosk, 2026-09-14, as *"an intermediate screen in Figma that is not in the app"*.
 *
 * ## Where it sits, and where it deliberately does not
 *
 * **The application journey only** — `submitted → payCardWhy → payCard`. The arrival menu's *Link pay
 * card* errand bypasses it — to the returning ask, {@link SCREEN.payCardAsk}. The evidence is set out on
 * `SCREEN.payCardWhy`; the short form is that the frame carries the journey rail, lives in the file's
 * new-user flow, and the prototype — which governs flow [0155] — wires the menu past this screen.
 *
 * ## What it does NOT have, and why that is the design rather than an omission
 *
 * - **No Back control.** The frame sets its `Back button` instance `visible: false` at all three widths,
 *   so the header row carries the status chip alone. `KioskCard` right-aligns a lone chip and holds the
 *   row at the 36px the frame keeps whether Back is in it or not.
 * - **No *Request assistance*, and therefore no queue entry** [0206: *never promise a person without
 *   queueing one; never queue one without being asked*]. The frame draws no help control on this screen
 *   and the prototype says why in as many words: *"This screen is instructions — nothing has gone wrong
 *   yet, so offering help here asks the candidate to judge a problem they have not met. Request assistance
 *   moved to the scanner, which is where the card can actually fail to read."* Arriving here promises
 *   nobody, so arriving here raises nobody. The `Need help?` pill `KioskScreen` renders is the inert FAB
 *   every screen from check-in onward carries, and it is disabled.
 * - **No `Action link →` under either callout.** Both instances are `visible: false` in the frame and
 *   neither has a destination in the file — the same placeholder `I9Screen` declined to build, for the
 *   same reason: a link to nowhere on an unattended kiosk is worse than no link.
 *
 * ⛔ **None of the copy on this screen is confirmed, and the frame does not confirm it.**
 * `04-eng-engineering-context/design-system.md` § *Figma is not the oracle for these* rules that copy is
 * governed by 0026 and 0040 and that **frame text is illustrative** — 0155's *"Figma governs the design"*
 * is about layout, spacing and structure. The English is transcribed from the frame so the screen is not
 * blank; the Spanish has no frame to transcribe from at any width and is the app's own, by the rule already
 * recorded on `i9.assistQuestion`. Both halves are owed a confirmation from ISS under 0040, string by
 * string, and the detail lives with the strings in `src/i18n/dictionary.ts`.
 */
export function PayCardWhyScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const goTo = useKioskSession((state) => state.goTo);

  return (
    <KioskScreen>
      <KioskCard
        band={<Stepper current={JOURNEY_STEP.getSetUp} />}
        footer={
          /*
            ONE action, right-aligned from `sm` up and full width below it — the frame's own `Actions`
            band at each width: HORIZONTAL with `primaryAxisAlignItems: MAX` around a 122x52 button at
            1440 and 820, VERTICAL around a 343-wide one at 375.

            The width is the column's doing, not the button's: `flex-col` stretches its child, `sm:flex-row`
            lets it size to its label. One element in the DOM at every width, the same idiom
            `PayCardScreen`'s footer already uses — rendering a mobile button and a desktop button side by
            side and hiding one would put two identically-named controls in the accessibility tree.
          */
          <div className="flex w-full flex-col sm:flex-row sm:items-center sm:justify-end">
            <Button
              onClick={() => {
                goTo(SCREEN.payCard);
              }}
            >
              {t('payCardWhy.continue')}
            </Button>
          </div>
        }
        /*
          The chip alone. The frame's `Back button` is `visible: false` here at 1440, 820 and 375, and
          `KioskCard` keys its right-alignment on `data-badge` for exactly this shape.
        */
        header={<StatusBadge label={t('badge.kiosk')} />}
        /*
          `banded`, not the default `spaced`, and it is measured rather than matched to the scan screen
          next door. Desktop (12389:15576): card 92, Logo 92→203, Stepper 203→247, Header 247→295,
          Content 295→859, Footer 859 — every join flush. iPad (12389:15677) is identical. Mobile
          (12389:15778) is the opposite: bar ends 52 → Logo 76 → ends 147 → `Rail (mobile)` 171 → ends 219
          → Content 243, three 24px gaps, because at 375 there is no card enclosing the slots.

          That is `banded` exactly — flush from `sm` up, 24px below it — and it is the same shape
          `10 · Submitted` is drawn as. It is NOT the rhythm the scan frames use; those stack flush at
          every width and 0152 ruled that a drawing error, which is why `PayCardScreen` keeps `spaced`.
        */
        rhythm="banded"
      >
        {/*
          `sm:pt-8` and `pb-8` are the frame's `Content` node, `pad[T32 R0 B32 L0]` at 1440 and 820. No
          `pt` below `sm`: the 375 frame draws `Content` at `pad[T0 R24 B32 L24]` and puts its 24px
          between the card's slots instead, which is `banded`'s job and not this column's.
        */}
        <div className="flex w-full flex-col items-start gap-6 pb-8 sm:pt-8">
          {/* The frame groups the title and its body as one block, 8px apart [`Heading`, 448x156]. */}
          <div className="flex w-full flex-col items-start gap-2">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-[var(--foreground)]"
              ref={heading}
              tabIndex={-1}
            >
              {t('payCardWhy.heading')}
            </h1>
            <p className="w-full text-sm leading-5 text-[var(--muted-foreground)]">
              {t('payCardWhy.intro')}
            </p>
          </div>

          {/*
            An ordered list, because the three instructions happen in an order: the candidate walks to the
            box, comes back, then checks digits. `<ol>` gives a screen reader the same count the teal
            numerals give everybody else.

            The markers are drawn rather than left to `list-style`, because the frame colours them
            `--primary` while the item text stays `--foreground`, and a CSS marker cannot be styled apart
            from its item in every browser this kiosk may run on. Same idiom as `I9Screen`'s two parts.
          */}
          {/*
            `role="list"` is NOT redundant: Safari removes list semantics from a list styled
            `list-style: none`, and this list is `list-none` for the reason above — so on the iPad this
            build targets, the "list, 3 items" a screen reader should announce silently would not be.
            jsdom computes the role from the tag and can never catch it.
          */}
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- NOT redundant on WebKit: Safari drops list semantics from a list styled `list-style: none`, and this list is `list-none` by design. */}
          <ol className="flex w-full list-none flex-col gap-6" role="list">
            {STEPS.map((key, index) => (
              <li className="flex w-full items-start gap-2" key={key}>
                <span aria-hidden="true" className="text-sm leading-5 text-[var(--primary)]">
                  {index + 1}
                </span>
                <span className="flex-1 text-sm leading-5 text-[var(--foreground)]">{t(key)}</span>
              </li>
            ))}
          </ol>

          {/* `Keep the card` — the brand-toned panel, #f3feff on #005d68 in the frame. */}
          <Callout>{t('payCardWhy.keepCard')}</Callout>
          {/*
            `Not yet — first payday` — the warning tone, #fffaf4 on #472b00. It is a different KIND of
            message from the one above it, not merely a second note: the first reassures, this one stops
            the candidate doing something that breaks their first pay run [0164].
          */}
          <Callout tone="warning">{t('payCardWhy.notYet')}</Callout>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
